import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	ArrowLeft,
	CheckSquare,
	AlertCircle,
	Paperclip,
	ChevronRight,
	RefreshCw,
	GitBranch,
	FileText,
	History,
} from 'lucide-react'
import { Button, Tabs } from '@components/common'
import {
	WorkflowOverviewTab,
	TaskDetailsTab,
	ActivityHistoryTab,
} from '@components/features/tasks'
import { useAuth } from '@context/AuthContext'
import { useTaskStore } from '@store'
import taskService from '@services/taskService'
import checklistService from '@services/checklistService'
import './TaskDetail.scss'

/**
 * TaskDetail Page (Unified)
 *
 * Combines workflow tracking and task details into a single tabbed interface:
 * - Tab 1: Task Details (Default) - Notes, documents, configuration, Submit for Review
 * - Tab 2: Workflow Overview - Progress, timeline, workflow steps, actions
 * - Tab 3: Activity History - All activity and history
 */
const TaskDetail = () => {
	const { taskId } = useParams()
	const navigate = useNavigate()
	const { user } = useAuth()

	const { startTask, submitTask, approveTask, rejectTask, acceptTask, completeTask } = useTaskStore()

	const [task, setTask] = useState(null)
	const [workflowInfo, setWorkflowInfo] = useState(null)
	const [workflow, setWorkflow] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [actionLoading, setActionLoading] = useState(false)
	const [activeTab, setActiveTab] = useState('task-details')

	// Check if user can configure tasks (CM/PM/Admin)
	const canConfigureTasks = useMemo(() => {
		if (!user) return false
		return ['super-admin', 'company-admin', 'compliance-manager', 'project-manager'].includes(user.role_slug)
	}, [user])

	// Check if task is locked
	const isLocked = useMemo(() => {
		if (!task) return true
		return task.is_locked === 1 || task.workflow_status === 'approved'
	}, [task])

	// Fetch all task data
	useEffect(() => {
		if (taskId) {
			fetchTaskData()
		}
	}, [taskId])

	const fetchTaskData = async () => {
		try {
			setLoading(true)
			setError(null)

			// Fetch both endpoints in parallel
			const [taskResponse, detailsResponse] = await Promise.all([
				taskService.getById(taskId),
				taskService.getTaskDetails(taskId),
			])

			// Process task data from /tasks/{id}
			if (taskResponse.success || taskResponse.status === 'success') {
				const taskData = taskResponse.data
				setTask(taskData)
				setWorkflowInfo(taskData.workflow_info)
			} else {
				throw new Error(taskResponse.message || 'Failed to load task')
			}

			// Process additional details from /checklists/{id}/task-details
			if (detailsResponse.status === 'success' || detailsResponse.success) {
				const detailsData = detailsResponse.data
				setWorkflow(detailsData.workflow)

				// Merge notes_count and documents_count into task
				setTask(prev => ({
					...prev,
					notes_count: detailsData.checklist?.notes_count || 0,
					documents_count: detailsData.checklist?.documents_count || 0,
				}))
			}
		} catch (err) {
			console.error('Error fetching task data:', err)
			setError(err.message || 'Failed to load task details')
		} finally {
			setLoading(false)
		}
	}

	// Handle task actions from WorkflowOverviewTab
	const handleTaskAction = async (action, data = {}) => {
		setActionLoading(true)
		try {
			let success = false

			switch (action) {
				case 'start':
					success = await startTask(task.id)
					break
				case 'submit':
					success = await submitTask(task.id, {
						notes: data.notes || '',
						evidence_attached: task.documents_count > 0,
					})
					break
				case 'accept':
					// Two-phase approval: Accept task first (changes status to pending_review)
					const acceptResult = await acceptTask(task.id)
					success = acceptResult && acceptResult.success
					break
				case 'approve':
					success = await approveTask(task.id, {
						notes: data.notes || '',
						evidence_attached: true,
					})
					break
				case 'complete':
					// Final step: Complete the task (marks workflow as 100% complete)
					success = await completeTask(task.id, {
						notes: data.notes || '',
						evidence_attached: task.documents_count > 0,
					})
					break
				case 'reject':
					success = await rejectTask(task.id, data.remarks)
					break
				default:
					console.warn('Unknown action:', action)
			}

			if (success) {
				await fetchTaskData()
			}
		} catch (err) {
			console.error(`Error performing ${action}:`, err)
		} finally {
			setActionLoading(false)
		}
	}

	// Handle task update from TaskDetailsTab
	const handleTaskUpdate = (updatedTask) => {
		setTask(prev => ({ ...prev, ...updatedTask }))
	}

	// Define tabs - Task Details first (default), then Workflow Overview, then Activity History
	const tabs = [
		{
			id: 'task-details',
			label: 'Task Details',
			icon: FileText,
			badge: task?.documents_count || undefined,
			content: (
				<TaskDetailsTab
					task={task}
					taskId={taskId}
					workflow={workflow}
					onTaskUpdate={handleTaskUpdate}
					onRefresh={fetchTaskData}
					onTaskAction={handleTaskAction}
					actionLoading={actionLoading}
				/>
			),
		},
		{
			id: 'workflow-overview',
			label: 'Workflow Overview',
			icon: GitBranch,
			content: (
				<WorkflowOverviewTab
					task={task}
					workflowInfo={workflowInfo}
					onTaskAction={handleTaskAction}
					actionLoading={actionLoading}
					documentsCount={task?.documents_count || 0}
				/>
			),
		},
		{
			id: 'activity-history',
			label: 'Activity History',
			icon: History,
			badge: task?.notes_count || undefined,
			content: (
				<ActivityHistoryTab
					task={task}
					workflowInfo={workflowInfo}
					taskId={taskId}
				/>
			),
		},
	]

	if (loading) {
		return (
			<div className="task-detail task-detail--loading">
				<div className="task-detail__spinner"></div>
				<p>Loading task details...</p>
			</div>
		)
	}

	if (error) {
		return (
			<div className="task-detail task-detail--error">
				<AlertCircle size={48} />
				<h2>Error Loading Task</h2>
				<p>{error}</p>
				<Button variant="outline" onClick={() => navigate(-1)}>
					<ArrowLeft size={16} />
					Go Back
				</Button>
			</div>
		)
	}

	if (!task) {
		return (
			<div className="task-detail task-detail--error">
				<AlertCircle size={48} />
				<h2>Task Not Found</h2>
				<p>The requested task could not be found.</p>
				<Button variant="outline" onClick={() => navigate(-1)}>
					<ArrowLeft size={16} />
					Go Back
				</Button>
			</div>
		)
	}

	return (
		<div className="task-detail">
			{/* Header */}
			<div className="task-detail__header">
				<div className="task-detail__breadcrumb">
					<Button variant="ghost" size="small" onClick={() => navigate(-1)}>
						<ArrowLeft size={16} />
						Back
					</Button>
					<span className="breadcrumb-separator">
						<ChevronRight size={14} />
					</span>
					<span className="breadcrumb-current">Task Details</span>
				</div>

				<div className="task-detail__title-row">
					<div className="task-detail__title-info">
						<h1 className="task-detail__title">
							<CheckSquare size={24} />
							{task.title || task.item_text || 'Untitled Task'}
						</h1>
						<div className="task-detail__badges">
							<span className={`status-badge status-badge--${task.workflow_status || 'not_started'}`}>
								{checklistService.getStatusLabel(task.workflow_status)}
							</span>
							{task.evidence_required === 1 && (
								<span className="status-badge status-badge--warning">
									<Paperclip size={12} />
									Evidence Required
								</span>
							)}
							{isLocked && (
								<span className="status-badge status-badge--secondary">Locked</span>
							)}
						</div>
					</div>

					<div className="task-detail__actions">
						<Button variant="ghost" size="small" onClick={fetchTaskData} disabled={loading}>
							<RefreshCw size={14} className={loading ? 'spinning' : ''} />
							Refresh
						</Button>
					</div>
				</div>
			</div>

			{/* Tabbed Content */}
			<div className="task-detail__tabs">
				<Tabs
					tabs={tabs}
					activeTab={activeTab}
					onTabChange={setActiveTab}
					variant="underline"
					size="medium"
				/>
			</div>
		</div>
	)
}

export default TaskDetail
